import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Loader2, Eye, EyeOff, AlertCircle, CheckCircle } from "lucide-react";
import OAuthSocialButtons from "./OAuthSocialButtons";
import { AuthInstrumentShell } from "./AuthInstrumentShell";
import {
  AUTH_PASSWORD_SCROLL_MARGIN_CLASS,
  AUTH_SIGN_IN_CARD_TESTID,
} from "../../lib/wwwPhoneClipLayout";
import { sanitizeReturnTo } from "../../../shared/safeReturnPath.js";
import {
  LOGIN_CHECK_EMAIL_HINT_TESTID,
  clearPendingEmailVerification,
  persistPendingEmailVerification,
  resolvePendingVerificationEmail,
} from "../../lib/pendingEmailVerification";
import { getAuthHeaders, getWebApiBase, persistWebSession } from "../../lib/webApi";
import i18n, { useTranslation } from "../../lib/i18n";

const API_BASE = getWebApiBase();

const SKIP_TO_MAIN_CLASS =
  "sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-white focus:text-black focus:px-4 focus:py-2 focus:rounded focus:shadow-lg";

function SkipToMainContent() {
  return (
    <a href="#main-content" className={SKIP_TO_MAIN_CLASS}>
      Skip to main content
    </a>
  );
}

function authUnavailableMessage() {
  return i18n.t("auth.errors.unableToReachService");
}

function toSafeAuthMessage(raw, fallback) {
  const resolvedFallback = fallback ?? authUnavailableMessage();
  if (!raw) return resolvedFallback;
  const message = String(raw).trim();
  if (!message) return resolvedFallback;
  const lower = message.toLowerCase();
  const looksHtml = /^<!doctype|^<html|^<body|^</i.test(message);
  if (
    looksHtml ||
    lower.includes("json parse") ||
    lower.includes("unexpected token <") ||
    lower.includes("unexpected character: <") ||
    lower.includes("internal server error")
  ) {
    return resolvedFallback;
  }
  return message.slice(0, 240);
}

async function parseApiResponse(response) {
  const contentType = response.headers.get("content-type") || "";
  const text = await response.text().catch(() => "");
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  const looksHtml =
    contentType.includes("text/html") || /^<!doctype|^<html|^</i.test(text.trim());
  return { data, looksHtml };
}

function Login() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [needsVerification, setNeedsVerification] = useState(false);
  const [showCheckEmailHint, setShowCheckEmailHint] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  // 2FA state (PRD-024)
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState("");
  const [twoFaCode, setTwoFaCode] = useState("");
  const twoFaInputRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const pendingHydratedRef = useRef(false);

  useEffect(() => {
    if (pendingHydratedRef.current) return;
    const email = resolvePendingVerificationEmail(location.state);
    if (!email) return;
    pendingHydratedRef.current = true;
    setFormData((prev) => ({ ...prev, email: prev.email || email }));
    setShowCheckEmailHint(true);
  }, [location.state]);

  const resolvePostLoginPath = (user) => {
    const needsOnboarding = !user?.onboardingCompletedAt;
    if (needsOnboarding) return "/onboarding";

    return sanitizeReturnTo(location.state?.from);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (error) setError("");
    if (needsVerification) setNeedsVerification(false);
  };

  const handleResendVerification = async () => {
    if (!formData.email) return;
    setResendLoading(true);
    setResendSent(false);
    try {
      const res = await fetch(`${API_BASE}/api/auth/resend-verification`, {
        method: "POST",
        headers: { ...getAuthHeaders("POST"), "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email }),
      });
      const { data, looksHtml } = await parseApiResponse(res);
      if (!data && looksHtml) throw new Error(authUnavailableMessage());
      if (!data) throw new Error(t("auth.emailVerification.resendFailed"));
      if (res.ok) setResendSent(true);
      else setError(toSafeAuthMessage(data.message || t("auth.emailVerification.resendFailed")));
    } catch (e) {
      setError(toSafeAuthMessage(e?.message, t("auth.emailVerification.resendFailed")));
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { ...getAuthHeaders("POST"), "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const { data, looksHtml } = await parseApiResponse(response);
      if (!data && looksHtml) {
        throw new Error(authUnavailableMessage());
      }
      if (!data) {
        throw new Error(authUnavailableMessage());
      }

      if (!response.ok) {
        if (data.requiresVerification) {
          setNeedsVerification(true);
          persistPendingEmailVerification(formData.email);
          setError(toSafeAuthMessage(data.message || t("auth.emailVerification.titlePending")));
          return;
        }
        throw new Error(toSafeAuthMessage(data.message || t("auth.errors.unableToSignIn")));
      }
      setNeedsVerification(false);

      // PRD-024: Check if 2FA is required
      if (data.requires2FA) {
        setRequires2FA(true);
        setTempToken(data.tempToken);
        setError("");
        return;
      }

      persistWebSession({
        token: data.token,
        refreshToken: data.refreshToken,
        user: data.user,
      });
      clearPendingEmailVerification();

      const nextPath = resolvePostLoginPath(data.user);
      navigate(nextPath, { replace: true });
    } catch (err) {
      console.error("Login error:", err);
      setError(toSafeAuthMessage(err?.message));
    } finally {
      setIsLoading(false);
    }
  };

  const _handleDemoLogin = async () => {
    // Use the real login flow with demo credentials
    setIsLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { ...getAuthHeaders("POST"), "Content-Type": "application/json" },
        body: JSON.stringify({
          email: "demo@cyclefuelpro.com",
          password: "demo123",
        }),
      });
      if (res.ok) {
        const { data } = await parseApiResponse(res);
        if (!data) throw new Error(authUnavailableMessage());
        persistWebSession({
          token: data.token,
          refreshToken: data.refreshToken,
          user: data.user,
        });
        clearPendingEmailVerification();
        navigate("/dashboard", { replace: true });
      } else {
        setError(
          t("auth.errors.unableToSignIn")
        );
      }
    } catch (err) {
      setError(toSafeAuthMessage(err?.message, authUnavailableMessage()));
    } finally {
      setIsLoading(false);
    }
  };

  // PRD-024: Handle 2FA code verification
  const handle2FAVerify = async (e) => {
    e?.preventDefault();
    if (twoFaCode.length < 6) return;
    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE}/api/auth/login/verify-2fa`, {
        method: "POST",
        headers: { ...getAuthHeaders("POST"), "Content-Type": "application/json" },
        body: JSON.stringify({ tempToken, code: twoFaCode }),
      });

      const { data, looksHtml } = await parseApiResponse(response);
      if (!data && looksHtml) throw new Error(authUnavailableMessage());
      if (!data) throw new Error(authUnavailableMessage());

      if (!response.ok) {
        throw new Error(toSafeAuthMessage(data.message || t("auth.errors.otpRequired")));
      }

      persistWebSession({
        token: data.token,
        refreshToken: data.refreshToken,
        user: data.user,
      });
      clearPendingEmailVerification();
      const nextPath = resolvePostLoginPath(data.user);
      navigate(nextPath, { replace: true });
    } catch (err) {
      setError(toSafeAuthMessage(err?.message, t("auth.errors.unableToSignIn")));
      setTwoFaCode("");
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-focus 2FA input when shown
  useEffect(() => {
    if (requires2FA && twoFaInputRef.current) {
      twoFaInputRef.current.focus();
    }
  }, [requires2FA]);

  // If 2FA is required, show the TOTP input screen
  if (requires2FA) {
    return (
      <>
        <SkipToMainContent />
        <AuthInstrumentShell showDemo={false}>
          <main id="main-content">
            <div className="instrument-fuel-tile w-full sm:p-6 lg:p-8" data-testid={AUTH_SIGN_IN_CARD_TESTID}>
            <div className="mb-6">
              <h1 className="font-display text-xl font-bold text-[var(--text-primary)]">
                {t("auth.twoFactor.title")}
              </h1>
            </div>

            <form
              onSubmit={handle2FAVerify}
              className="space-y-5"
              aria-busy={isLoading}
            >
              <div>
                <label htmlFor="login-2fa-code" className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
                  {t("auth.twoFactor.codeLabel")}
                </label>
                <input
                  id="login-2fa-code"
                  ref={twoFaInputRef}
                  type="text"
                  inputMode="numeric"
                  value={twoFaCode}
                  onChange={(e) =>
                    setTwoFaCode(
                      e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 8)
                    )
                  }
                  placeholder="000000"
                  maxLength={8}
                  className="w-full px-4 py-3.5 border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 rounded-xl text-center text-2xl sm:text-3xl font-mono tracking-[0.4em] focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder:text-stone-300 dark:placeholder:text-stone-600"
                  autoComplete="one-time-code"
                />
                <p className="text-xs text-stone-400 dark:text-stone-500 text-center mt-2">
                  Or enter an 8-character backup code
                </p>
              </div>

              <motion.button
                type="submit"
                disabled={isLoading || twoFaCode.length < 6}
                className="w-full bg-primary-500 hover:bg-primary-600 text-white text-base sm:text-lg font-semibold py-3 rounded-xl disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin shrink-0" aria-hidden />
                    {t("auth.twoFactor.verifying")}
                  </>
                ) : (
                  t("auth.twoFactor.verify")
                )}
              </motion.button>
            </form>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-start gap-2 p-3 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-xl mt-4"
                role="alert"
              >
                <AlertCircle
                  className="w-4 h-4 text-danger-600 dark:text-danger-400 shrink-0 mt-0.5"
                  aria-hidden
                />
                <span className="text-danger-700 dark:text-danger-400 font-medium text-sm">
                  {error}
                </span>
              </motion.div>
            )}

            {success && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-start gap-2 p-3 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-xl mt-4"
                role="status"
              >
                <CheckCircle
                  className="w-4 h-4 text-success-600 dark:text-success-400 shrink-0 mt-0.5"
                  aria-hidden
                />
                <span className="text-success-700 dark:text-success-400 font-medium text-sm">
                  {success}
                </span>
              </motion.div>
            )}

            <div className="mt-5 pt-5 border-t border-stone-200 dark:border-stone-700">
              <button
                type="button"
                onClick={() => {
                  setRequires2FA(false);
                  setTempToken("");
                  setTwoFaCode("");
                  setError("");
                }}
                className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors"
              >
                {t("auth.twoFactor.backToSignIn")}
              </button>
            </div>
            </div>
          </main>
        </AuthInstrumentShell>
      </>
    );
  }

  return (
    <>
      <SkipToMainContent />
      <AuthInstrumentShell>
        <main id="main-content">
            <div className="instrument-fuel-tile w-full sm:p-6 lg:p-8" data-testid={AUTH_SIGN_IN_CARD_TESTID}>
          <div className="mb-5">
            <h1 className="font-display text-xl font-bold text-[var(--text-primary)]">
              {t("auth.login.signInButton")}
            </h1>
          </div>
          {showCheckEmailHint && (
            <div
              className="rounded-2xl mb-5 p-4 bg-[var(--bg-muted)] space-y-2"
              role="status"
              data-testid={LOGIN_CHECK_EMAIL_HINT_TESTID}
            >
              <p className="flex items-center gap-2 text-[var(--text-primary)] font-semibold">
                <CheckCircle
                  className="w-4 h-4 text-success-600 dark:text-success-400 shrink-0"
                  aria-hidden
                />
                {t("auth.emailVerification.titlePending")}
              </p>
              <p className="text-sm text-[var(--text-secondary)]">
                {t("auth.emailVerification.sentLead")}{" "}
                <strong className="break-all">
                  {formData.email || t("auth.emailVerification.emailFallback")}
                </strong>
                . {t("auth.emailVerification.instructions")}
              </p>
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={!formData.email || resendLoading || resendSent}
                className="text-sm font-medium min-h-[44px] text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 disabled:opacity-50"
              >
                {resendLoading
                  ? t("auth.emailVerification.resending")
                  : resendSent
                    ? t("auth.emailVerification.resentSuccess")
                    : t("auth.emailVerification.resendCta")}
              </button>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4" aria-busy={isLoading}>
            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-semibold text-[var(--text-primary)] mb-2"
              >
                {t("auth.login.emailLabel")}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                className="input-modern px-4"
                placeholder={t("auth.login.emailPlaceholder")}
              />
            </div>

            {/* Password Field */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-semibold text-[var(--text-primary)] mb-2"
              >
                {t("auth.login.passwordLabel")}
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`input-modern pl-4 pr-24 ${AUTH_PASSWORD_SCROLL_MARGIN_CLASS}`}
                  placeholder={t("auth.login.passwordPlaceholder")}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center gap-1 min-h-[44px] text-xs font-semibold text-primary-600 dark:text-primary-400"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-pressed={showPassword}
                  aria-label={showPassword ? t("common.hide") : t("common.show")}
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4 shrink-0" aria-hidden />
                  ) : (
                    <Eye className="w-4 h-4 shrink-0" aria-hidden />
                  )}
                  <span className="hidden sm:inline">
                    {showPassword ? t("common.hide") : t("common.show")}
                  </span>
                </button>
              </div>
            </div>

            {/* Forgot Password Link */}
            <div className="flex items-center justify-between">
              <Link
                to="/forgot-password"
                className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors"
              >
                {t("auth.login.forgotPassword")}
              </Link>
            </div>

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary text-base py-3 min-h-[44px] inline-flex items-center justify-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin shrink-0" aria-hidden />
                  {t("auth.login.signingIn")}
                </>
              ) : (
                t("auth.login.signInButton")
              )}
            </motion.button>
          </form>

          <OAuthSocialButtons
            returnTo={sanitizeReturnTo(location.state?.from)}
            disabled={isLoading}
          />

          {/* Error/Success Messages */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col gap-3 p-3 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-xl mt-4"
              role="alert"
            >
              <span className="flex items-start gap-2 text-danger-700 dark:text-danger-300 font-medium text-sm">
                <AlertCircle
                  className="w-4 h-4 text-danger-600 dark:text-danger-400 shrink-0 mt-0.5"
                  aria-hidden
                />
                {error}
              </span>
              {needsVerification && !showCheckEmailHint && (
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={resendLoading || resendSent}
                  className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 disabled:opacity-50"
                >
                  {resendLoading
                    ? t("auth.emailVerification.resending")
                    : resendSent
                    ? t("auth.emailVerification.titlePending")
                    : t("auth.emailVerification.resendCta")}
                </button>
              )}
            </motion.div>
          )}

          {success && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-start gap-2 p-3 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-xl mt-4"
              role="status"
            >
              <CheckCircle
                className="w-4 h-4 text-success-600 dark:text-success-400 shrink-0 mt-0.5"
                aria-hidden
              />
              <span className="text-success-700 dark:text-success-300 font-medium text-sm">
                {success}
              </span>
            </motion.div>
          )}

          {/* Sign Up Link */}
          <div className="mt-4 pt-4 border-t border-stone-200/80 dark:border-stone-700/80 text-center">
            <p className="text-[var(--text-secondary)] text-sm">
              {t("auth.login.noAccount")}{" "}
              <Link
                to="/register"
                className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold transition-colors"
              >
                {t("auth.login.signUp")}
              </Link>
            </p>
          </div>
          </div>
        </main>
      </AuthInstrumentShell>
    </>
  );
}

export default Login;
